import { useAdvancedBounceRate } from '@va/dashboard/api-client/visitor-stats';
import { useSessionMetricsFilters } from '@va/dashboard/shared/filters';
import { useLocalizationContext } from '@va/localization';
import { FiltersContextProvider } from '@va/shared/feature-filters';
import { TimePeriod } from '@va/types/time';
import { DateComparisonPeriod } from '@va/ui/components/report-block';
import { addNumberSeparator, formatBounceRatePercentage } from '@va/util/helpers';
import { useCallback } from 'react';
import { useCurrentVisitorStatsParams, usePreviousVisitorStatsParams } from '../expandable-rb/useVisitorStatsParams';
import { VisitorStatsReport } from '../expandable-rb/VisitorStatsReport';

const useCurrent = (unit: TimePeriod = TimePeriod.day) => {
  const params = useCurrentVisitorStatsParams();
  return useAdvancedBounceRate({ ...params, unit });
};
const usePrevious = (activeComparisonOption: DateComparisonPeriod, unit: TimePeriod = TimePeriod.day) => {
  const params = usePreviousVisitorStatsParams(activeComparisonOption);
  return useAdvancedBounceRate({ ...params, unit });
};

const AdvancedBounceRateCard = ({
  id,
  gridPosition,
  canExpand,
}: {
  id: string;
  gridPosition: number;
  canExpand?: boolean;
}) => {
  const { locale, translate } = useLocalizationContext();
  const { allFilters } = useSessionMetricsFilters();

  const valueMapper = useCallback(
    (val: number) => addNumberSeparator(val, locale, { style: 'percent', maximumFractionDigits: 1 }),
    [locale],
  );

  const headingValueMapper = useCallback((val: number) => formatBounceRatePercentage(val, locale), [locale]);

  return (
    <FiltersContextProvider allFilterOptions={allFilters} reportBlockId={id}>
      <VisitorStatsReport
        id={id}
        gridPosition={gridPosition}
        canExpand={canExpand}
        fetchCurrentHook={useCurrent}
        fetchPreviousHook={usePrevious}
        valueMapper={valueMapper}
        headingValueMapper={headingValueMapper}
        title={translate('card.advancedBounceRate.title')}
        titleTooltip={translate('card.advancedBounceRate.tooltip')}
        reversePercentageColors
        showTimePeriodFilter
      />
    </FiltersContextProvider>
  );
};

export default AdvancedBounceRateCard;
